import { useContext, useEffect, useMemo, useState } from 'react';
import { FormControlLabel, FormGroup, styled, Switch, Typography } from '@mui/material';
import { isEmpty, isEqual } from 'lodash';
import { useParams } from 'react-router-dom';
import { captableAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import { captableUrl } from 'common/config/urls';
import { DEFAULT_CURRENCY } from 'common/constants/cap-table';
import {
  BREAKPOINT_ANALYSIS_LOCKED_MESSAGE,
  CAP_TABLE_NEEDS_MORE_SECURITIES_TAGLINE,
  CAP_TABLE_NEEDS_MORE_SECURITIES_TITLE,
  FORBIDDEN_BP_ANALYSIS,
  GET_BP_ACCESS,
} from 'common/constants/cap-table/capTable';
import { CONVERTIBLE_NOTES, UNISSUED_OPTIONS } from 'common/constants/securityTypes';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { Alert, AllocationFinalMessage, MessageBox } from 'components';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { GridSkeleton } from 'components/Grid';
import { LayoutContext } from 'context';
import GoToCapTable from 'pages/CapTable/cap-table/components/GoToCapTable';
import { CaptableHk } from 'services/hooks';
import { worldCurrencies } from 'utilities';
import { BreakpointAnalysisProps } from './BreakpointAnalysis.props';
import { classes, Root } from './BreakpointAnalysis.styles';
import CustomBreakpoints from './components/CustomBreakpoints/CustomBreakpoints';
import WaterfallBreakpoints from './components/WaterfallBreakpoints/WaterfallBreakpoints';
import { BreakpointColumn, BreakpointSeries } from './types/breakpoint-analysis.models';
import replaceProRata from './utilities/replaceProRata';

const BreakpointAnalysis = (props: BreakpointAnalysisProps) => {
  const [globalStore] = useStore() as unknown as UseStoreValues;

  const { viewOnlyUser } = useContext(LayoutContext);
  const { firmSlugParam, companySlugParam } = useParams<{ firmSlugParam: string; companySlugParam: string }>();
  const { isShowLoadingProgress } = globalStore;
  const captableInfo = globalStore.captableInfo ?? {};
  const [waterfallColumns, setWaterfallColumns] = useState<BreakpointColumn[] | null>(null);
  const [customBreakpointColumns, setCustomBreakpointColumns] = useState<BreakpointColumn[] | null>([]);
  const [showSecurityTypeMsg, setShowSecurityTypeMsg] = useState(false);
  const [customBreakpoints, setCustomBreakpoints] = useState(captableInfo?.use_custom_breakpoints || false);
  const [isDisabled, setIsDisabled] = useState<boolean>();
  const [isAllocationFinal, setIsAllocationFinal] = useState<boolean>();

  const { useGetBreakpointAnalysis } = CaptableHk;
  const [updatedCaptableInfo, getCapTableBreakpoints] = useGetBreakpointAnalysis(captableInfo.id);
  const { selectedMeasurementDate, saveAsNewProforma } = props;
  const [format] = useFormat({ page: 'captable', units: REGULAR_UNIT });
  const { currency } = format;
  const { symbol: currencySymbol } = currency;

  const capTableURL = useMemo(() => captableUrl(firmSlugParam, companySlugParam), [firmSlugParam, companySlugParam]);

  const getAllocationStatus = async () => {
    try {
      if (!captableInfo.id) {
        return;
      }
      const response = await captableAction.verifyIfHasFinalAllocation(captableInfo.id);
      setIsDisabled(!isEmpty(response) || viewOnlyUser);
      setIsAllocationFinal(!isEmpty(response));
    } catch (error: any) {
      setIsDisabled(false);
      setIsAllocationFinal(false);
      throw new Error(error);
    }
  };

  useEffect(() => {
    if (captableInfo.id && !isEmpty(captableInfo.securities) && isEmpty(updatedCaptableInfo)) {
      const optionConvertibleSecurities = captableInfo.securities?.filter(s =>
        [UNISSUED_OPTIONS, CONVERTIBLE_NOTES].includes(s.security_type ?? -1)
      );

      if (
        optionConvertibleSecurities
        && captableInfo.securities
        && optionConvertibleSecurities.length < captableInfo.securities.length
      ) {
        getCapTableBreakpoints(captableInfo.id);
      } else {
        setShowSecurityTypeMsg(true);
      }
    }
    if (captableInfo.id) {
      getAllocationStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo.id, viewOnlyUser]);

  useEffect(() => {
    if (!isEmpty(captableInfo)) {
      setCustomBreakpoints(captableInfo?.use_custom_breakpoints ?? false);
      setCustomBreakpointColumns(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo.id, captableInfo.use_custom_breakpoints]);

  const getColumnsData = (breakpointsData: BreakpointColumn[], setInitialValue = false): BreakpointColumn[] => {
    const getSerieData = (serieItem: BreakpointSeries) => {
      if (!Number.isNaN(serieItem.value)) {
        return {
          ...serieItem,
          value: setInitialValue ? 0 : serieItem.value,
        };
      }
      return serieItem;
    };

    return breakpointsData.map(bp => ({
      ...bp,
      price: setInitialValue ? 0 : bp.price,
      series: bp.series.map(serie => ({
        ...getSerieData(serie),
        security: serie.security,
      })),
      range: bp.range.replaceAll(worldCurrencies[DEFAULT_CURRENCY], currencySymbol),
    }));
  };

  useEffect(() => {
    if (!isEmpty(captableInfo)) {
      if (captableInfo?.waterfall_breakpoints?.breakpoints) {
        const waterfallBreakpoints = captableInfo.waterfall_breakpoints.breakpoints;
        const tmpBreakpoints = getColumnsData(waterfallBreakpoints);
        setWaterfallColumns(tmpBreakpoints);
      } else {
        setWaterfallColumns(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo?.id]);

  useEffect(() => {
    if (!isEmpty(captableInfo) && customBreakpoints && !isEmpty(waterfallColumns)) {
      let tmpBreakpoints = null;
      if (captableInfo?.custom_breakpoints?.breakpoints) {
        const customBreakpointsData = captableInfo.custom_breakpoints.breakpoints;
        tmpBreakpoints = getColumnsData(customBreakpointsData);
        const lastBreakpoint = tmpBreakpoints[tmpBreakpoints.length - 1];
        const lastBreakpointSeries = lastBreakpoint.series;
        // Use current own percent as value for existing custom breakpoints when the final bp is PRO RATA
        const replacedSeries = replaceProRata(lastBreakpointSeries, captableInfo.securities ?? []);
        lastBreakpoint.series = replacedSeries;
      } else {
        tmpBreakpoints = getColumnsData(waterfallColumns!, true);
      }
      setCustomBreakpointColumns(tmpBreakpoints);
    } else {
      setCustomBreakpointColumns(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo, customBreakpoints, waterfallColumns]);

  const breakPointAnalysisDetailTableProps = useMemo(
    () => ({
      data: updatedCaptableInfo,
      waterfallColumns: waterfallColumns!,
      format,
      hideGrid: customBreakpoints,
    }),
    [updatedCaptableInfo, waterfallColumns, format, customBreakpoints]
  );

  const customBreakpointsDetailTableProps = {
    captableId: captableInfo.id!,
    hideGrid: !customBreakpoints,
    customBreakpointColumns: customBreakpointColumns!,
    selectedMeasurementDate,
    saveAsNewProforma,
    format,
    isAllocationFinal: !!isAllocationFinal,
    isDisabled: !!isDisabled,
  };

  const handleChangeSwitch = () => setCustomBreakpoints(customBPs => !customBPs);

  if (isEqual(updatedCaptableInfo, ERROR_403)) {
    return <MessageBox title={FORBIDDEN_BP_ANALYSIS} tagline={GET_BP_ACCESS} fullWidth={false} />;
  }

  if (captableInfo?.id && (isEmpty(captableInfo.securities) || showSecurityTypeMsg)) {
    let title = 'The Cap Table is empty';
    let message = 'Please add a security first.';

    if (showSecurityTypeMsg) {
      title = CAP_TABLE_NEEDS_MORE_SECURITIES_TITLE;
      message = CAP_TABLE_NEEDS_MORE_SECURITIES_TAGLINE;
    }

    return (
      <MessageBox
        title={title}
        tagline={message}
        fullWidth={false}
        action={<GoToCapTable capTableURL={capTableURL} />}
      />
    );
  }

  if (!captableInfo?.id || isEmpty(updatedCaptableInfo)) {
    return <GridSkeleton />;
  }

  return (
    <Root className="grid-bottom-margin">
      {/* Allocation Final Message */}
      {isAllocationFinal && isDisabled && (
        <AllocationFinalMessage
          tableTerms={{ tableName: 'Breakpoint Analysis' }}
          setIsDisabled={setIsDisabled}
          singleAlert
          finalAllocations={[]}
          viewOnlyUser={viewOnlyUser}
        />
      )}
      {isAllocationFinal && !isDisabled && <Alert isAlertVisible>{BREAKPOINT_ANALYSIS_LOCKED_MESSAGE}</Alert>}
      <FormGroup row className={classes.useCustomBreakpoints}>
        <FormControlLabel
          control={
            <Switch
              id="custom-breakpoints-switch"
              checked={customBreakpoints || false}
              onChange={handleChangeSwitch}
              name="useCustomBreakpoints"
              color="primary"
              disabled={isShowLoadingProgress || isDisabled}
              disableRipple
            />
          }
          label={
            <Typography variant="body2" color="textPrimary" className={classes.switchToggleLegend}>
              Use custom breakpoints?
            </Typography>
          }
        />
      </FormGroup>
      <CustomBreakpoints {...customBreakpointsDetailTableProps} />
      <WaterfallBreakpoints {...breakPointAnalysisDetailTableProps} />
    </Root>
  );
};

export default BreakpointAnalysis;
